import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { COLOR_HEX } from "./colors";

const POLL_MS = 5000;

const fmt = (n) => (Number.isInteger(n) ? n : n.toFixed(2)).toString();

const Swatch = ({ color, size = 28 }) => (
  <span
    style={{ background: COLOR_HEX[color] || "#ccc", width: size, height: size, borderColor: color === "White" ? "#e5e5e5" : "transparent" }}
    className="inline-block rounded-full border shrink-0"
  />
);

const Cinc = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fabricFilter, setFabricFilter] = useState("All");
  const [colorFilter, setColorFilter] = useState("All");
  const [q, setQ] = useState("");
  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({ quantity_kg: "", client_name: "", company_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const flashRef = useRef({});
  const [flash, setFlash] = useState({});

  const load = async () => {
    try {
      const [it, sm] = await Promise.all([api.get("/cinc/items"), api.get("/cinc/summary")]);
      setItems((prev) => {
        // flash items whose availability changed
        const changed = {};
        it.data.forEach((n) => {
          const old = prev.find((p) => p.id === n.id);
          if (old && old.available_kg !== n.available_kg) changed[n.id] = true;
        });
        if (Object.keys(changed).length) {
          setFlash(changed);
          clearTimeout(flashRef.current.t);
          flashRef.current.t = setTimeout(() => setFlash({}), 1500);
        }
        return it.data;
      });
      setSummary(sm.data);
    } catch (e) {
      /* ignore transient */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const fabrics = useMemo(() => Array.from(new Set(items.map((i) => i.fabric))), [items]);
  const colors = useMemo(() => Array.from(new Set(items.map((i) => i.color))), [items]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (fabricFilter !== "All" && i.fabric !== fabricFilter) return false;
      if (colorFilter !== "All" && i.color !== colorFilter) return false;
      if (q && !`${i.fabric} ${i.color}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, fabricFilter, colorFilter, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => {
      (g[i.fabric] = g[i.fabric] || []).push(i);
    });
    return g;
  }, [filtered]);

  const openBooking = (item) => {
    setBooking(item);
    setForm({ quantity_kg: "", client_name: "", company_name: "", phone: "" });
    setErr("");
    setOk("");
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setErr("");
    const qty = parseFloat(form.quantity_kg);
    if (!qty || qty < 0.5) return setErr("Minimum booking is 0.5 kg");
    if (qty > booking.available_kg) return setErr(`Only ${fmt(booking.available_kg)} kg available`);
    if (!form.client_name.trim() || !form.phone.trim()) return setErr("Client name and phone are required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/cinc/book", {
        item_id: booking.id,
        quantity_kg: qty,
        client_name: form.client_name,
        company_name: form.company_name,
        phone: form.phone,
      });
      setOk(`Booked ${fmt(qty)} kg of ${booking.fabric} · ${booking.color}. ${fmt(data.remaining_kg)} kg left.`);
      await load();
      setTimeout(() => setBooking(null), 1400);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Booking failed. Please try again.");
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1A1A17]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap" rel="stylesheet" />

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#1A1A17] text-[#F4F1EA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span style={{ fontFamily: "'Fraunces', serif" }} className="text-3xl font-semibold tracking-tight">CiNC</span>
            <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.2em] text-[#C9B98F]">Live Fabric Availability</span>
          </div>
          <div className="flex items-center gap-2 text-right">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#C9B98F]">Live</span>
          </div>
        </div>
      </header>

      {/* Summary */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Total available", summary ? `${fmt(summary.total_available_kg)} kg` : "—"],
            ["Booked so far", summary ? `${fmt(summary.booked_kg)} kg` : "—"],
            ["Varieties", summary ? summary.item_count : "—"],
            ["Updated", "every 5s"],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-[#E4DFD3] rounded-lg p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">{k}</p>
              <p style={{ fontFamily: "'Fraunces', serif" }} className="text-2xl md:text-3xl font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mt-6">
          <input
            data-testid="cinc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search fabric or colour…"
            className="flex-1 bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#1A1A17]"
          />
          <select data-testid="cinc-fabric-filter" value={fabricFilter} onChange={(e) => setFabricFilter(e.target.value)} className="bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 text-sm outline-none">
            <option>All</option>
            {fabrics.map((f) => <option key={f}>{f}</option>)}
          </select>
          <select data-testid="cinc-color-filter" value={colorFilter} onChange={(e) => setColorFilter(e.target.value)} className="bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 text-sm outline-none">
            <option>All</option>
            {colors.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Inventory */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8 pb-24">
        {Object.keys(grouped).sort().map((fab) => (
          <section key={fab} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <h2 style={{ fontFamily: "'Fraunces', serif" }} className="text-xl font-semibold">{fab}</h2>
              <div className="flex-1 h-px bg-[#E4DFD3]" />
              <span className="font-mono text-[11px] text-[#8A8577]">{grouped[fab].length} colours</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {grouped[fab].map((it) => {
                const pct = it.initial_kg > 0 ? Math.max(0, Math.min(100, (it.available_kg / it.initial_kg) * 100)) : 0;
                const out = it.available_kg <= 0;
                const low = !out && it.available_kg <= (summary?.low_stock_kg ?? 10);
                return (
                  <div
                    key={it.id}
                    data-testid={`cinc-item-${it.id}`}
                    className={`bg-white border rounded-lg p-4 transition-shadow ${flash[it.id] ? "border-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.25)]" : "border-[#E4DFD3]"}`}
                  >
                    <div className="flex items-center gap-3">
                      <Swatch color={it.color} />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{it.color}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">{it.fabric}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <p style={{ fontFamily: "'Fraunces', serif" }} className={`text-3xl font-semibold ${out ? "text-[#B0AB9C]" : "text-[#1A1A17]"}`} data-testid={`cinc-avail-${it.id}`}>
                          {fmt(it.available_kg)}<span className="text-base text-[#8A8577] ml-1">kg</span>
                        </p>
                        {low && <p className="font-mono text-[10px] uppercase tracking-widest text-orange-600 mt-1">Low stock</p>}
                        {out && <p className="font-mono text-[10px] uppercase tracking-widest text-red-600 mt-1">Sold out</p>}
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#EFEBE0] rounded-full mt-3 overflow-hidden">
                      <div className={`h-full rounded-full ${out ? "bg-red-400" : low ? "bg-orange-400" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <button
                      disabled={out}
                      onClick={() => openBooking(it)}
                      data-testid={`cinc-book-${it.id}`}
                      className="mt-4 w-full py-2.5 rounded-lg text-sm font-medium bg-[#1A1A17] text-[#F4F1EA] hover:bg-[#33322c] transition-colors disabled:bg-[#D8D3C6] disabled:text-[#8A8577] disabled:cursor-not-allowed"
                    >
                      {out ? "Unavailable" : "Book this fabric"}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {Object.keys(grouped).length === 0 && (
          <p className="text-center py-20 text-[#8A8577]" style={{ fontFamily: "'Fraunces', serif" }}>No fabric matches your filters.</p>
        )}
      </div>

      {/* Booking modal */}
      {booking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => !submitting && setBooking(null)}>
          <div className="bg-[#F4F1EA] w-full md:max-w-md rounded-t-2xl md:rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="cinc-booking-modal">
            <div className="bg-[#1A1A17] text-[#F4F1EA] px-6 py-5 flex items-center gap-3">
              <Swatch color={booking.color} size={32} />
              <div>
                <p style={{ fontFamily: "'Fraunces', serif" }} className="text-xl font-semibold">{booking.fabric} · {booking.color}</p>
                <p className="font-mono text-[11px] text-[#C9B98F]">{fmt(booking.available_kg)} kg available now</p>
              </div>
            </div>
            <form onSubmit={submitBooking} className="p-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Quantity (kg) · min 0.5</label>
                <input type="number" step="0.5" min="0.5" value={form.quantity_kg} onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })} data-testid="cinc-qty" required
                  className="w-full mt-1 bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" placeholder="e.g. 5" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Client name *</label>
                <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="cinc-client" required
                  className="w-full mt-1 bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Company name</label>
                <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="cinc-company"
                  className="w-full mt-1 bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Phone *</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cinc-phone" required
                  className="w-full mt-1 bg-white border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" />
              </div>
              {err && <p className="text-red-600 text-sm" data-testid="cinc-book-error">{err}</p>}
              {ok && <p className="text-emerald-700 text-sm" data-testid="cinc-book-success">{ok}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setBooking(null)} disabled={submitting} className="flex-1 py-3 rounded-lg border border-[#D8D3C6] text-sm font-medium">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="cinc-confirm-booking" className="flex-1 py-3 rounded-lg bg-[#1A1A17] text-[#F4F1EA] text-sm font-medium hover:bg-[#33322c] disabled:opacity-60">
                  {submitting ? "Booking…" : "Confirm booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cinc;
